<!--
	Blocks are rendered in projection order. `@if` chains rather than `@switch` so the
	template type-checker narrows the DisplayBlock union reliably under strictTemplates.
-->
@for (block of blocks(); track $index) {
	<div class="oasis-block" [class.mb-4]="!$last" [class.mb-0]="$last">
		@if (block.heading) {
			<div class="fw-semibold text-body-secondary text-uppercase small mb-2" role="heading" [attr.aria-level]="blockLevel()">
				{{ block.heading }}
			</div>
		}

		<!-- Label / value pairs -->
		@if (block.kind === 'fields') {
			<dl class="row mb-0">
				@for (field of block.fields; track field.label) {
					<dt class="col-sm-5 col-lg-4 fw-semibold small text-body-secondary">{{ field.label }}</dt>
					<dd class="col-sm-7 col-lg-8 mb-2">
						<ng-container [ngTemplateOutlet]="valueTpl" [ngTemplateOutletContext]="{ $implicit: field.value }" />
						@if (field.hint) {
							<div class="form-text mt-0">{{ field.hint }}</div>
						}
					</dd>
				}
			</dl>
		}

		<!-- Repeating records: employment rows, timeline entries, references -->
		@if (block.kind === 'entries') {
			@if (block.entries.length) {
				<div class="vstack gap-3">
					@for (entry of block.entries; track $index) {
						<div class="border rounded p-3">
							<div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
								<div>
									<div class="fw-semibold" role="heading" [attr.aria-level]="entryLevel()">{{ entry.title }}</div>
									@if (entry.subtitle) {
										<div class="text-body-secondary small">{{ entry.subtitle }}</div>
									}
								</div>
								@if (entry.badges?.length) {
									<div class="d-flex flex-wrap gap-1">
										@for (badge of entry.badges; track badge) {
											<span class="badge bg-secondary-subtle text-secondary-emphasis">{{ badge }}</span>
										}
									</div>
								}
							</div>
							@if (entry.fields.length) {
								<dl class="row mb-0">
									@for (field of entry.fields; track field.label) {
										<dt class="col-sm-5 col-lg-4 fw-semibold small text-body-secondary">{{ field.label }}</dt>
										<dd class="col-sm-7 col-lg-8 mb-2">
											<ng-container [ngTemplateOutlet]="valueTpl" [ngTemplateOutletContext]="{ $implicit: field.value }" />
											@if (field.hint) {
												<div class="form-text mt-0">{{ field.hint }}</div>
											}
										</dd>
									}
								</dl>
							}
						</div>
					}
				</div>
			} @else {
				<p class="text-body-secondary mb-0">{{ block.emptyText }}</p>
			}
		}

		<!--
			Check-all-that-apply. Only selected items are listed; the count carries the
			denominator so a short list is not mistaken for a truncated one.
		-->
		@if (block.kind === 'checklist') {
			<p class="text-body-secondary small mb-2">{{ checklistSummary(block.selected.length, block.totalAvailable) }}</p>
			@if (block.selected.length) {
				<ul class="list-unstyled mb-0 vstack gap-2">
					@for (item of block.selected; track item.label) {
						<li>
							<span class="me-1" aria-hidden="true">&check;</span>{{ item.label }}
							@if (item.description) {
								<div class="text-body-secondary small ms-3 oasis-longtext">{{ item.description }}</div>
							}
						</li>
					}
				</ul>
			}
		}

		<!-- Uploaded documents. The host resolves a download URL; this only reports the request. -->
		@if (block.kind === 'files') {
			@if (block.files.length) {
				<ul class="list-group list-group-flush">
					@for (file of block.files; track file.s3Key; let fileIndex = $index) {
						<li class="list-group-item px-0">
							<div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
								<div>
									<div class="fw-semibold">{{ file.filename }}</div>
									<div class="text-body-secondary small">{{ file.docTypeLabel }}</div>
									@if (file.description) {
										<div class="text-body-secondary small oasis-longtext">{{ file.description }}</div>
									}
								</div>
								<div class="text-body-secondary small text-sm-end">
									<div>{{ fileSize(file.sizeBytes) }}</div>
									<!-- A real instant, unlike the date-only fields, so the date pipe is safe here. -->
									<div>Uploaded {{ file.uploadedAtIso | date: 'medium' }}</div>
									@if (filesDownloadable()) {
										<button
											type="button"
											class="btn btn-sm btn-outline-primary mt-2"
											[disabled]="isDownloading(file)"
											[attr.aria-label]="downloadLabel(file)"
											[attr.aria-describedby]="errorFor(file) ? errorId(fileIndex) : null"
											(click)="fileDownloadRequested.emit(file)"
										>
											@if (isDownloading(file)) {
												<span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
											}
											View/Download
										</button>
									}
								</div>
							</div>
							@if (filesDownloadable()) {
								<!-- The spinner is hidden from screen readers, so these carry the state instead. -->
								@if (isDownloading(file)) {
									<div class="text-body-secondary small mt-1" role="status">Preparing {{ file.filename }}…</div>
								}
								@if (errorFor(file); as message) {
									<div class="text-danger small mt-1" role="alert" [id]="errorId(fileIndex)">{{ message }}</div>
								}
							}
						</li>
					}
				</ul>
			} @else {
				<p class="text-body-secondary mb-0">{{ block.emptyText }}</p>
			}
		}

		<!-- Standalone prose: a gap explanation, an applicant note -->
		@if (block.kind === 'note') {
			<p class="mb-0 oasis-longtext">{{ block.text }}</p>
		}
	</div>
}

<!-- One place where a DisplayValue becomes markup, shared by both dl renderings. -->
<ng-template #valueTpl let-value>
	@if (value.kind === 'text') {
		<span>{{ value.value }}</span>
	} @else if (value.kind === 'longText') {
		<div class="oasis-longtext">{{ value.value }}</div>
	} @else if (value.kind === 'date') {
		<span>{{ isoDate(value.iso) }}</span>
	} @else if (value.kind === 'dateRange') {
		<span>{{ isoRange(value.fromIso, value.toIso, value.ongoing) }}</span>
	} @else if (value.kind === 'list') {
		<ul class="list-inline mb-0">
			@for (item of value.items; track item) {
				<li class="list-inline-item badge bg-secondary-subtle text-secondary-emphasis">{{ item }}</li>
			}
		</ul>
	} @else if (value.kind === 'link') {
		<a [href]="value.href" target="_blank" rel="noopener noreferrer">{{ value.text }}</a>
	} @else if (value.kind === 'file') {
		<span>{{ value.file.filename }}</span>
		<span class="text-body-secondary small ms-2">{{ value.file.docTypeLabel }}</span>
	} @else if (value.kind === 'empty') {
		<!--
			Muted and italic so a reader scanning for answers can skip absences, but still
			real text rather than a blank cell — a blank reads as a rendering fault.
		-->
		<span class="text-body-secondary fst-italic">{{ absenceText(value) }}</span>
	}
</ng-template>
